import { FC, useState } from 'react';
import { Button } from 'react-daisyui';

const DECLINE_TAGS = [
  { value: 'not_available', label: 'Not available' },
  { value: 'too_far', label: 'Too far' },
  { value: 'rate_mismatch', label: 'Rate mismatch' },
  { value: 'other', label: 'Other' },
] as const;

type DeclineTag = (typeof DECLINE_TAGS)[number]['value'];

type DeclineBookingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  familyName: string;
  isLoading?: boolean;
};

const DeclineBookingModal: FC<DeclineBookingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  familyName,
  isLoading = false,
}) => {
  const [selectedTag, setSelectedTag] = useState<DeclineTag | null>(null);
  const [otherNote, setOtherNote] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedTag) return;
    const reason =
      selectedTag === 'other' && otherNote.trim()
        ? `Other: ${otherNote.trim()}`
        : DECLINE_TAGS.find((t) => t.value === selectedTag)!.label;
    onConfirm(reason);
  };

  const handleClose = () => {
    setSelectedTag(null);
    setOtherNote('');
    onClose();
  };

  const isConfirmDisabled =
    isLoading || !selectedTag || (selectedTag === 'other' && !otherNote.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5">
        <div>
          <h2 className="text-lg font-semibold">Add a quick reason if declining</h2>
          <p className="text-sm text-gray-500 mt-1">
            Declining request from <strong>{familyName}</strong>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {DECLINE_TAGS.map((tag) => (
            <button
              key={tag.value}
              type="button"
              onClick={() => setSelectedTag(tag.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer ${
                selectedTag === tag.value
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
              }`}
            >
              {tag.label}
            </button>
          ))}
        </div>

        {selectedTag === 'other' && (
          <textarea
            value={otherNote}
            onChange={(e) => setOtherNote(e.target.value)}
            placeholder="Briefly explain..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-black text-sm"
          />
        )}

        <div className="flex gap-3">
          <Button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 bg-white text-black border border-gray-300 hover:bg-gray-50 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="flex-1 bg-[#FF5861] text-white border-none hover:opacity-80 rounded-xl disabled:opacity-40"
          >
            {isLoading ? 'Declining...' : 'Decline'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeclineBookingModal;
